<script setup lang="ts">
import type { PerformanceThresholds } from '~/features/my-wallet/types/charts'
import { buildPerformanceRanges } from '~/features/my-wallet/utils/charts'

interface Props {
  percentage: number | null
  thresholds: PerformanceThresholds
  label: string
  inverted?: boolean
  muted?: boolean
  commercialException?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  inverted: false,
  muted: false,
  commercialException: false,
})

type ArcTone = 'model-c' | 'model-b' | 'model-a' | 'muted' | 'waived' | 'late'

interface Arc {
  from: number
  to: number
  tone: ArcTone
}

const ARC_PATH = 'M 9.15 61 A 51.85 51.85 0 0 1 112.85 61'
const STROKE_WIDTH = 18.3

const clampedPercentage = computed(() => Math.max(0, Math.min(100, props.percentage ?? 0)))

const arcs = computed<Arc[]>(() => {
  if (props.percentage === null)
    return []

  if (props.inverted)
    return [{ from: clampedPercentage.value, to: 100, tone: props.muted ? 'muted' : 'late' }]

  if (props.muted)
    return [{ from: 0, to: clampedPercentage.value, tone: 'muted' }]

  return buildPerformanceRanges({
    percentage: clampedPercentage.value,
    thresholds: props.thresholds,
    showModelA: true,
    commercialException: props.commercialException,
  })
})

const visibleArcs = computed(() => arcs.value.filter(arc => arc.to > arc.from))

function dashArray(arc: Arc) {
  return `${arc.to - arc.from} 100`
}

function dashOffset(arc: Arc) {
  return -arc.from
}
</script>

<template>
  <svg
    class="performance-gauge"
    viewBox="0 0 122 61"
    width="122"
    height="61"
    role="img"
    :aria-label="label"
  >
    <path
      class="performance-gauge-track"
      :d="ARC_PATH"
      fill="none"
      :stroke-width="STROKE_WIDTH"
      pathLength="100"
    />
    <path
      v-for="arc in visibleArcs"
      :key="arc.tone"
      class="performance-gauge-arc"
      :class="`-${arc.tone}`"
      :d="ARC_PATH"
      fill="none"
      :stroke-width="STROKE_WIDTH"
      pathLength="100"
      :stroke-dasharray="dashArray(arc)"
      :stroke-dashoffset="dashOffset(arc)"
    />
  </svg>
</template>

<style scoped lang="scss">
@use '../../styles/model-tones' as tones;

.performance-gauge {
  @apply flex-shrink-0;

  .performance-gauge-track {
    @apply text-neutral-high-medium;
    stroke: currentColor;
  }

  .performance-gauge-arc {
    stroke: currentColor;
    @include tones.tone-modifiers(text, ('model-c', 'model-b', 'model-a', 'muted', 'waived', 'late'));
  }
}
</style>
